import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { object, date } from "yup";
import moment from "moment";
import AdminFormModal from "../../AdminFormModal";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import { ReactComponent as DelegationIcon } from "../../../assets/images/delegate.svg";
import { addToast } from "../../../helpers";
import api from "../../../providers/api";

import "./style.scss";

const { Field } = Form;

const today = new Date();
today.setHours(0, 0, 0, 0);

const validationSchema = object().shape({
  FromDate: date().required(" ").min(today, " ").nullable(),
  ToDate: date()
    .required(" ")
    .when(["FromDate"], (fromDate, schema) => {
      if (fromDate) {
        return schema.min(fromDate, " ");
      }
    })
    .nullable(),
  ToUser: object().required(" ").nullable(),
});

const DelegationForm = ({ data, users, closeModal, callback }) => {
  const [initialValues] = useState(
    data
      ? {
          ...data,
          ToDate: new Date(data.ToDate),
          FromDate: new Date(data.FromDate),
        }
      : {
          ToUser: "",
          ToDate: "",
          FromDate: "",
        }
  );

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const body = {
        ...values,
        FromDate: moment(values.FromDate).locale("en").format("MM/DD/YYYY"),
        ToDate: moment(values.ToDate).locale("en").format("MM/DD/YYYY"),
        ToUser: values.ToUser,
      };
      const service = data ? "update" : "create";
      const res = await api.delegation[service](body);
      const { Data, StatusCode } = res;
      if (StatusCode === "Success") {
        callback(Data, service);
        addToast.success(data ? "toast.successUpdate" : "toast.successSave");
        closeModal();
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminFormModal
      title={<FormattedMessage id="manage-delegation" />}
      Icon={DelegationIcon}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, isSubmitting, isValid }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="delegation-form-body"
            >
              <Space
                direction="vertical"
                size="medium"
                className="form-container"
              >
                <Row>
                  <Field
                    type="select"
                    name="ToUser"
                    label={<FormattedMessage id="manage-to" />}
                    inputProps={{
                      isLoading: !users?.length,
                      options: users,
                      getOptionValue: (item) => item.UserName,
                      getOptionLabel: (item) => item.DisplayName,
                      placeholder: <FormattedMessage id="manage-to" />,
                    }}
                    hideErrorMessage
                    fixedLabel
                    required
                  />
                </Row>
                <Row size="small">
                  <Field
                    type="date"
                    name="FromDate"
                    label={<FormattedMessage id="delegations.start-date" />}
                    inputProps={{
                      minDate: today,
                      maxDate: values.ToDate,
                    }}
                    hideErrorMessage
                    fixedLabel
                    required
                  />
                  <Field
                    type="date"
                    name="ToDate"
                    label={<FormattedMessage id="delegations.end-date" />}
                    inputProps={{
                      minDate: values.FromDate || today,
                    }}
                    hideErrorMessage
                    fixedLabel
                    required
                  />
                </Row>
              </Space>
              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {data ? (
                    <FormattedMessage id="common.update" />
                  ) : (
                    <FormattedMessage id="common.add" />
                  )}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default DelegationForm;
